<template>
  <v-container fluid grid-list-xs>

    <AccountTerminalDebug :terminal="selectedTerminal"></AccountTerminalDebug>

    <v-expansion-panel
      v-model="panel"
      expand
      multiple
      class="elevation-0"
    >
      <v-expansion-panel-content>
        <div slot="header" class="title font-weight-medium">
          <v-chip
            pill
            color="primary"
          >
            <h3 class="white--text">1</h3>
          </v-chip>
          Details
        </div>
        <v-card>
          <v-card-text>
            <v-layout row>
              <v-flex xs12>
                <v-layout row wrap>

                  <v-flex xs11>
                    <div class="caption grey--text text--darken-1 font-weight-medium mt-2 mb-1">SCHEDULE STATUS</div>
                  </v-flex>
                  <v-flex xs1 class="text-xs-right">
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on }">
                        <v-btn icon flat small color="primary" @click="printSchedule" class="ma-0 pa-0" v-on="on">
                          <v-icon>fa-print</v-icon>
                        </v-btn>
                      </template>
                      <span>Print Schedule</span>
                    </v-tooltip>
                  </v-flex>

                  <v-flex xs6 sm3 class="primary--text">Status:</v-flex>
                  <v-flex xs6 sm3 class="text-capitalize">{{ schedule.recurring_status }}</v-flex>
                  <v-flex xs6 sm3 class="primary--text">Frequency:</v-flex>
                  <v-flex xs6 sm3 class="text-capitalize">{{ schedule.recurring_type || '-' }}</v-flex>
                  <v-flex xs6 sm3 class="primary--text">Behavior:</v-flex>
                  <v-flex xs6 sm3 class="text-capitalize">{{ installmentBehaviorLabel }}</v-flex>
                  <template v-if="parsedCustom && parsedCustom.migrated_from_transaction_id && hasPermission('can_view_migration')">
                    <v-flex xs6 sm3 class="primary--text">Migration:</v-flex>
                    <v-flex xs6 sm3>
                      <router-link :to="{ path: '/plans/manage', query: { transaction_id: parsedCustom.migrated_from_transaction_id } }">
                        <v-chip small outline color="info">FAPS #{{ parsedCustom.migrated_from_transaction_id }}</v-chip>
                      </router-link>
                    </v-flex>
                  </template>

                  <v-flex xs12><v-divider class="my-2"></v-divider></v-flex>

                  <v-flex xs12>
                    <div class="caption grey--text text--darken-1 font-weight-medium mt-2 mb-1">FINANCIAL SUMMARY</div>
                  </v-flex>

                  <v-flex xs6 sm3 class="primary--text">Total:</v-flex>
                  <v-flex xs6 sm3>{{ money(schedule.installment_total_amount ? schedule.installment_total_amount / 100 : 0) }}</v-flex>
                  <template v-if="downpaymentTransaction && downpaymentTransaction.total_amount">
                    <v-flex xs6 sm3 class="primary--text">Downpayment:</v-flex>
                    <v-flex xs6 sm3>
                      {{ money((downpaymentTransaction.total_amount - downpaymentTransaction.fee_amount) / 100) }}
                      <span class="pl-2 primary--text" style="cursor:pointer;" @click="showDownpaymentPriceAdjustmentFee=true" v-if="selectedTerminal.is_cashdiscount && schedule.request_type !== 'ach' && !showDownpaymentPriceAdjustmentFee && !schedule.is_fee_waived">Price Adj Fee &or;</span>
                      <span class="pl-2 primary--text" style="cursor:pointer;" @click="showDownpaymentPriceAdjustmentFee=false" v-if="selectedTerminal.is_cashdiscount && schedule.request_type !== 'ach' && showDownpaymentPriceAdjustmentFee && !schedule.is_fee_waived">{{ money((downpaymentTransaction.fee_amount) / 100) }} &and;</span>
                    </v-flex>
                  </template>

                  <v-flex xs6 sm3 class="primary--text">Installment Amt:</v-flex>
                  <v-flex xs6 sm3>
                    {{ money((schedule.total_amount - schedule.fee_amount) / 100) }}
                    <span class="pl-2 primary--text" style="cursor:pointer;" @click="showAmountPriceAdjustmentFee=true" v-if="selectedTerminal.is_cashdiscount && schedule.request_type !== 'ach' && !showAmountPriceAdjustmentFee && !schedule.is_fee_waived">Price Adj Fee &or;</span>
                    <span class="pl-2 primary--text" style="cursor:pointer;" @click="showAmountPriceAdjustmentFee=false" v-if="selectedTerminal.is_cashdiscount && schedule.request_type !== 'ach' && showAmountPriceAdjustmentFee && !schedule.is_fee_waived">{{ money(schedule.fee_amount / 100) }} &and;</span>
                  </v-flex>
                  <v-flex xs6 sm3 class="primary--text">Paid / Settled:</v-flex>
                  <v-flex xs6 sm3>{{ money(totalAmountSettledLessFee / 100) }}</v-flex>

                  <v-flex xs6 sm3 class="primary--text">Remaining Balance:</v-flex>
                  <v-flex xs6 sm3>{{ money(remainingBalance) }}</v-flex>
                  <v-flex xs6 sm3 class="primary--text">Payments:</v-flex>
                  <v-flex xs6 sm3>{{ remainingPaymentCount }} remaining</v-flex>

                  <v-flex xs12><v-divider class="my-2"></v-divider></v-flex>

                  <v-flex xs12>
                    <div class="caption grey--text text--darken-1 font-weight-medium mt-2 mb-1">SCHEDULE TIMELINE</div>
                  </v-flex>

                  <v-flex xs6 sm3 class="primary--text">Start Date:</v-flex>
                  <v-flex xs6 sm3>{{ schedule.start ? dateUtc(schedule.start) : '-' }}</v-flex>
                  <v-flex xs6 sm3 class="primary--text">Finish Date:</v-flex>
                  <v-flex xs6 sm3>
                    <span v-if="schedule.end">{{ dateUtc(schedule.end) }}</span>
                    <span v-else>ongoing</span>
                  </v-flex>
                  <v-flex xs6 sm3 class="primary--text">Next Run:</v-flex>
                  <v-flex xs6 sm3>{{ schedule.next_run && schedule.recurring_status !== 'cancelled' && schedule.recurring_status !== 'ended' ? dateUtc(schedule.next_run) : '-' }}</v-flex>

                  <v-flex xs12><v-divider class="my-2"></v-divider></v-flex>

                  <v-flex xs12>
                    <div class="caption grey--text text--darken-1 font-weight-medium mt-2 mb-1">PAYMENT METHOD</div>
                  </v-flex>

                  <v-flex xs6 sm3 class="primary--text">{{ schedule.payment_type ? schedule.payment_type.toUpperCase() : (schedule.request_type === 'ach' ? 'ACH' : 'Card') }}:</v-flex>
                  <v-flex xs6 sm3>**** **** **** {{ schedule.last_four }}</v-flex>
                  <template v-if="schedule.request_type !== 'ach'">
                    <v-flex xs6 sm3 class="primary--text">Exp Date:</v-flex>
                    <v-flex xs6 sm3>{{ expiration(schedule.card_expiration_yymm) }}</v-flex>
                  </template>

                  <v-flex xs12><v-divider class="my-2"></v-divider></v-flex>

                  <v-flex xs12>
                    <div class="caption grey--text text--darken-1 font-weight-medium mt-2 mb-1">CUSTOMER</div>
                  </v-flex>

                  <v-flex xs6 sm3 class="primary--text">Billing:</v-flex>
                  <v-flex xs6 sm9>
                    <span v-if="schedule.billing_name">{{ schedule.billing_name }}</span>
                    <span v-if="schedule.billing_address"> {{ schedule.billing_address }}</span>
                    <span v-if="schedule.billing_city || schedule.billing_state || schedule.billing_postal_code">
                      {{ schedule.billing_city }},
                      {{ schedule.billing_state }}
                      {{ schedule.billing_postal_code }}
                    </span>
                    <span v-if="schedule.billing_country"> {{ schedule.billing_country }}</span>
                  </v-flex>
                  <template v-if="schedule.billing_email">
                    <v-flex xs6 sm3 class="primary--text">Email:</v-flex>
                    <v-flex xs6 sm9>{{ schedule.billing_email }}</v-flex>
                  </template>
                  <template v-if="schedule.billing_phone">
                    <v-flex xs6 sm3 class="primary--text">Phone:</v-flex>
                    <v-flex xs6 sm9>{{ schedule.billing_phone }}</v-flex>
                  </template>
                  <template v-if="schedule.billing_ip_address">
                    <v-flex xs6 sm3 class="primary--text">IP Address:</v-flex>
                    <v-flex xs6 sm9>{{ schedule.billing_ip_address }}</v-flex>
                  </template>

                  <template v-if="schedule.order_number || schedule.customer_po || schedule.custom_field_1 || schedule.custom_field_2 || schedule.custom_field_3">
                    <v-flex xs12><v-divider class="my-2"></v-divider></v-flex>

                    <v-flex xs12>
                      <div class="caption grey--text text--darken-1 font-weight-medium mt-2 mb-1">CUSTOM FIELDS</div>
                    </v-flex>

                    <template v-if="schedule.order_number">
                      <v-flex xs6 sm3 class="primary--text">Order Number:</v-flex>
                      <v-flex xs6 sm3>{{ schedule.order_number }}</v-flex>
                    </template>
                    <template v-if="schedule.customer_po">
                      <v-flex xs6 sm3 class="primary--text">Customer PO:</v-flex>
                      <v-flex xs6 sm3>{{ schedule.customer_po }}</v-flex>
                    </template>
                    <template v-if="schedule.custom_field_1">
                      <v-flex xs6 sm3 class="primary--text">Custom 1:</v-flex>
                      <v-flex xs6 sm3>{{ schedule.custom_field_1 }}</v-flex>
                    </template>
                    <template v-if="schedule.custom_field_2">
                      <v-flex xs6 sm3 class="primary--text">Custom 2:</v-flex>
                      <v-flex xs6 sm3>{{ schedule.custom_field_2 }}</v-flex>
                    </template>
                    <template v-if="schedule.custom_field_3">
                      <v-flex xs6 sm3 class="primary--text">Custom 3:</v-flex>
                      <v-flex xs6 sm3>{{ schedule.custom_field_3 }}</v-flex>
                    </template>
                  </template>

                </v-layout>
              </v-flex>
            </v-layout>
            <v-divider class="primary"></v-divider>
          </v-card-text>

          <v-card-actions>
            <v-btn block flat outline color="primary" @click="viewPaymentHistory" class="overflow-button"><span>View Installments</span></v-btn>
            <v-btn block flat outline color="primary" @click="cancelSchedule" class="overflow-button" :disabled="schedule.recurring_status === 'cancelled' || schedule.recurring_status === 'ended'"><span>Cancel Installments</span></v-btn>
            <v-btn block flat outline color="primary" @click="takePayment" class="overflow-button" :disabled="schedule.recurring_status === 'cancelled' || schedule.recurring_status === 'ended'"><span>Take Payment</span></v-btn>
            <v-btn block flat outline color="primary" @click="modifySchedule" class="overflow-button" :disabled="schedule.recurring_status === 'cancelled' || schedule.recurring_status === 'ended'"><span>Modify / Change</span></v-btn>
          </v-card-actions>
        </v-card>
      </v-expansion-panel-content>

      <v-expansion-panel-content>
        <div slot="header" class="title font-weight-medium">
          <v-chip
            pill
            color="primary"
          >
            <h3 class="white--text">2</h3>
          </v-chip>
          Schedule of Installments
        </div>

        <Schedule :schedule="schedule" :futurePendingPayments="futurePendingPayments" :paymentHistory="paymentHistory" :fapsTransactions="fapsTransactions" :downpaymentTransaction="downpaymentTransaction"></Schedule>
      </v-expansion-panel-content>
    </v-expansion-panel>

    <v-dialog v-model="payModal" maxWidth="785px" persistent @keydown.esc.prevent="closePayModal" @input="v => v || closePayModal">
      <v-toolbar dense>
        <v-toolbar-title>
          TAKE <span class="font-weight-thin">PAYMENT</span>
          <template v-if="payStep === 2">
            <span class="font-weight-thin">IN <span class="primary--text">FULL</span></span>
          </template>
        </v-toolbar-title>

        <v-spacer></v-spacer>
        <span class="subheading"><span class="primary--text">Remaining balance</span> to pay <span class="caption" v-if="selectedTerminal.is_cashdiscount && schedule.request_type !== 'ach' && !schedule.is_fee_waived">(including Price Adj Fee) </span>is <span class="font-weight-medium">{{ money(remainingBalance) }}</span></span>

        <v-btn icon flat color="primary" @click="closePayModal">
          <v-icon>close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-window v-model="payStep">
        <v-window-item :value="1">
          <v-card>
            <v-card-text fill-height>
              You have selected TAKE PAYMENT. The following options are available to you. Each has important implications. Please click checkbox.
              <v-checkbox
                label="Take ONE payment in FULL to clear account"
                v-model="payType[0]"
                class="ml-4"
                @click.native.stop="payTypeChanged($event, 0)"
                color="primary"
                dense
                hide-details
              ></v-checkbox>
              <v-checkbox
                label="Take additional payment (amount can vary) or reduce monthly amount/schedule"
                v-model="payType[1]"
                class="ml-4"
                @click.native.stop="payTypeChanged($event, 1)"
                color="primary"
                dense
                hide-details
              ></v-checkbox>
              <v-layout row wrap justify-end mt-4>
                <v-btn class="primary" @click="setPayStep">OK</v-btn>
                <v-btn @click="closePayModal">Cancel</v-btn>
              </v-layout>
            </v-card-text>
          </v-card>
        </v-window-item>

        <v-window-item :value="2">
          <v-card>
            <Payment v-if="payStep === 2" :terminal="selectedTerminal" :schedule="schedule" :amountRemaining="remainingBalance" :payFull="true" :transactionGroupId="transactionGroupId" :installmentTotalAmount="installmentTotalAmount"></Payment>
          </v-card>
        </v-window-item>

        <v-window-item :value="3">
          <v-card>
            <Payment v-if="payStep === 3" :terminal="selectedTerminal" :schedule="schedule" :amountRemaining="remainingBalance" :payFull="false" :transactionGroupId="transactionGroupId" :installmentTotalAmount="installmentTotalAmount"></Payment>
          </v-card>
        </v-window-item>
      </v-window>
    </v-dialog>

    <v-dialog v-model="modifyModal" maxWidth="750px" persistent @keydown.esc.prevent="closeModifyModal" @input="v => v || closeModifyModal">
      <v-toolbar dense>
        <v-toolbar-title>
          <template v-if="modifyStep === 2">MODIFY <span class="font-weight-light">INSTALLMENT <span class="primary--text">PAYMENT</span></span></template>
          <template v-else-if="modifyStep === 3">MODIFY <span class="font-weight-light">BILLING <span class="primary--text">ADDRESS</span></span></template>
          <template v-else-if="modifyStep === 4">MODIFY <span class="font-weight-light">PAYMENT <span class="primary--text">INFORMATION</span></span></template>
          <template v-else>MODIFY / CHANGE</template>
        </v-toolbar-title>

        <v-spacer></v-spacer>
        <span class="font-weight-light title">{{ schedule.card_name }}</span>

        <v-btn icon flat color="primary" @click="closeModifyModal">
          <v-icon>close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-window v-model="modifyStep">
        <v-window-item :value="1">
          <v-card>
            <v-card-text fill-height>
              You have selected MODIFY / CHANGE. The following options are available to you. Each has important implications. Please click checkbox. Press OK to continue.
              <v-checkbox
                label="MODIFY Installment Schedule"
                v-model="modifyType[0]"
                class="ml-4"
                @click.native.stop="modifyTypeChanged($event, 0)"
                color="primary"
                dense
                hide-details
              ></v-checkbox>
              <v-checkbox
                label="MODIFY Billing Address / Email"
                v-model="modifyType[1]"
                class="ml-4"
                @click.native.stop="modifyTypeChanged($event, 1)"
                color="primary"
                dense
                hide-details
              ></v-checkbox>
              <v-checkbox
                label="MODIFY Payment Information"
                v-model="modifyType[2]"
                class="ml-4"
                @click.native.stop="modifyTypeChanged($event, 2)"
                color="primary"
                dense
                hide-details
              ></v-checkbox>
              <v-layout row wrap justify-end mt-4>
                <v-btn class="primary" @click="setModifyStep">OK</v-btn>
                <v-btn @click="closeModifyModal">Cancel</v-btn>
              </v-layout>
            </v-card-text>
          </v-card>
        </v-window-item>

        <v-window-item :value="2">
          <v-card>
            <ModifyInstallments v-if="schedule.id" :terminal="selectedTerminal" :schedule="schedule" :scheduledTransactions="scheduledTransactions"></ModifyInstallments>
          </v-card>
        </v-window-item>

        <v-window-item :value="3">
          <v-card>
            <ModifyBilling v-if="schedule.id" :terminal="selectedTerminal" :schedule="schedule"></ModifyBilling>
          </v-card>
        </v-window-item>

        <v-window-item :value="4">
          <v-card>
            <ModifyPayment v-if="schedule.id" :terminal="selectedTerminal" :schedule="schedule"></ModifyPayment>
          </v-card>
        </v-window-item>
      </v-window>
    </v-dialog>

    <SchedulePrint ref="schedulePrint" :schedule="schedule"></SchedulePrint>
  </v-container>
</template>

<script>
import Moment from 'moment'

import Bus from '@/bus'

import ApiMixin from '@/mixins/api'
import AuthMixin from '@/mixins/auth'
import CashDiscountMixin from '@/mixins/cashDiscount'
import DisplayMixin from '@/mixins/display'
import RecurringMixin from '@/mixins/recurring'
import StoreMixin from '@/store/mixin'
import UtilityMixin from '@/mixins/utility'

import AccountTerminalDebug from '@/components/terminal/components/AccountTerminalDebug.vue'
import ModifyBilling from '@/components/pnxInstallments/ModifyBilling.vue'
import ModifyInstallments from '@/components/pnxInstallments/ModifyInstallments.vue'
import ModifyPayment from '@/components/pnxInstallments/ModifyPayment.vue'
import Payment from '@/components/pnxInstallments/Payment.vue'
import Schedule from '@/components/pnxInstallments/Schedule.vue'
import SchedulePrint from '@/components/pnxInstallments/SchedulePrint.vue'

export default {
  mixins: [ApiMixin, AuthMixin, CashDiscountMixin, DisplayMixin, RecurringMixin, StoreMixin, UtilityMixin],
  components: {AccountTerminalDebug, ModifyBilling, ModifyInstallments, ModifyPayment, Payment, Schedule, SchedulePrint},

  data: function () {
    return {
      panel: [true, false],

      selectedTerminal: {},
      terminalId: null,

      schedule: {},
      scheduledTransactions: [],
      futurePendingPayments: [],
      paymentHistory: [],
      fapsTransactions: [],

      showAmountPriceAdjustmentFee: false,
      downpaymentTransaction: {},
      showDownpaymentPriceAdjustmentFee: false,

      payModal: false,
      payType: [true, false],
      payStep: 1,
      modifyModal: false,
      modifyType: [true, false, false],
      modifyStep: 1
    }
  },

  beforeDestroy () {
    Bus.$off('closeModifyModal', this.closeModifyModal)
    Bus.$off('showModifyModal', this.modifySchedule)
    Bus.$off('closePayModal', this.closePayModal)
    Bus.$off('reloadPnxInstallment', this.load)
  },

  mounted () {
    Bus.$on('closeModifyModal', this.closeModifyModal)
    Bus.$on('showModifyModal', this.modifySchedule)
    Bus.$on('closePayModal', this.closePayModal)
    Bus.$on('reloadPnxInstallment', this.load)

    this.load()
  },

  computed: {
    parsedCustom () {
      try {
        return this.schedule.custom ? JSON.parse(this.schedule.custom) : null
      } catch (e) {
        return null
      }
    },

    terminal: {
      get () {
        return this.terminalId
      },
      set (value) {
        if (this.schedule.request_type === 'ach') {
          let terminal = this.terminalsACHCanCharge.find(terminal => terminal.terminal_id === value)
          if (terminal) {
            this.terminalId = value
            this.selectedTerminal = terminal
          }
        } else {
          let terminal = this.terminalsCardCanCharge.find(terminal => terminal.terminal_id === value)
          if (terminal) {
            this.terminalId = value
            this.selectedTerminal = terminal
          }
        }
      }
    },

    remainingBalance: function () {
      return Math.max(0, Number(((this.schedule.installment_remaining_amount || 0) / 100).toFixed(2)))
    },

    remainingPaymentCount: function () {
      return this.futurePendingPayments ? this.futurePendingPayments.length : 0
    },

    transactionGroupId: function () {
      return this.schedule.transaction_group_custom_tracking_id || null
    },

    installmentTotalAmount: function () {
      if (this.schedule.installment_total_amount) {
        return this.schedule.installment_total_amount / 100
      }
      return 0
    },

    totalAmountSettledLessFee: function () {
      return Math.max(0, Number(this.schedule.installment_settled_amount || 0))
    },

    installmentBehaviorLabel: function () {
      let value = this.schedule.installment_behavior
      if (value === 'end_early') return 'End Early'
      if (value === 'keep_schedule') return 'Keep Schedule'
      return value || '-'
    }
  },

  methods: {
    async load () {
      Bus.$emit('openWaitModal')

      if (this.terminalsCardCanCharge && this.terminalsCardCanCharge.length) {
        this.terminal = this.terminalsCardCanCharge[0].terminal_id
      } else if (this.terminalsACHCanCharge && this.terminalsACHCanCharge.length) {
        this.terminal = this.terminalsACHCanCharge[0].terminal_id
      }

      if (!this.$route.query.id) {
        Bus.$emit('openModal', {
          status: 'error',
          message: 'Invalid schedule'
        })
        return false
      }

      try {
        let data = await this.api({url: `/schedules/${this.$route.query.id}`})
        this.schedule = data.data.schedule

        if (this.schedule.request_type === 'ach') {
          let achTerminal = this.terminalsACHCanCharge.find(t => t.terminal_id == this.schedule.terminal_id)
          if (!achTerminal && this.schedule.processor_id) {
            achTerminal = this.terminalsACHCanCharge.find(t => t.processor_id == this.schedule.processor_id)
          }
          if (achTerminal) {
            this.terminal = achTerminal.terminal_id
          }
        } else {
          let cardTerminal = this.terminalsCardCanCharge.find(t => t.terminal_id == this.schedule.terminal_id)
          if (!cardTerminal && this.schedule.processor_id) {
            cardTerminal = this.terminalsCardCanCharge.find(t => t.processor_id == this.schedule.processor_id)
          }
          if (cardTerminal) {
            this.terminal = cardTerminal.terminal_id
          }
        }

        let allScheduled = this.getScheduledPayments(this.schedule)
        this.scheduledTransactions = allScheduled.filter(function (value) {
          let now = Moment.utc().subtract(1, 'days')
          return Moment(value).isAfter(now)
        })

        try {
          let futureData = await this.api({url: `/schedules/${this.schedule.id}/future-payments`})
          this.futurePendingPayments = futureData.data.rows || []
        } catch (err) {
          this.futurePendingPayments = []
          console.error('Failed to load future payments:', err)
          Bus.$emit('openModal', {
            status: 'error',
            message: 'Could not load future payment schedule. Remaining payment information may be inaccurate.'
          })
        }

        try {
          let historyData = await this.api({
            url: `/schedules/${this.schedule.id}/transactions`,
            params: { perPage: 100 }
          })
          this.paymentHistory = historyData.data.rows || []
        } catch (err) {
          this.paymentHistory = []
          console.error('Failed to load payment history:', err)
          Bus.$emit('openModal', {
            status: 'error',
            message: 'Could not load payment history. Schedule details may be incomplete.'
          })
        }

        try {
          let customData = this.schedule.custom ? JSON.parse(this.schedule.custom) : null
          if (customData && customData.migrated_from_transaction_id && this.hasPermission('can_view_migration')) {
            let txnData = await this.api({url: '/transactions/' + customData.migrated_from_transaction_id})
            let groupId = txnData.data.transaction.transaction_group_custom_tracking_id
            if (groupId) {
              let planData = await this.api({url: `/plans/${groupId}`})
              this.fapsTransactions = planData.data.group.transactions || []
            }
          }
        } catch (err) {
          this.fapsTransactions = []
        }

        if (this.fapsTransactions.length) {
          let candidates = this.fapsTransactions.filter(
            t => (t.request_type === 'sale' || t.request_type === 'ach') &&
                 t.api_method !== 'cron' && t.voided === null &&
                 t.is_recurring === 0 && t.future_post === null
          )
          this.downpaymentTransaction = candidates.length
            ? candidates.sort((a, b) => new Date(a.authorized) - new Date(b.authorized))[0]
            : {}
        } else if (this.paymentHistory.length) {
          let scheduleRecord = this.paymentHistory.find(t => t.request_type === 'schedule')
          if (scheduleRecord) {
            let dp = this.paymentHistory
              .filter(t => (t.request_type === 'sale' || t.request_type === 'ach') && t.voided === null)
              .sort((a, b) => a.id - b.id)[0]
            if (dp && dp.id !== scheduleRecord.id) {
              this.downpaymentTransaction = dp
            }
          }
        }
      } catch (err) {
        Bus.$emit('openModal', {
          status: 'error',
          message: err
        })
      }

      Bus.$emit('closeWaitModal')
    },

    printSchedule () {
      this.$refs.schedulePrint.print()
    },

    viewPaymentHistory () {
      this.$set(this.panel, 1, true)
    },

    async cancelSchedule () {
      if (!this.schedule || !this.schedule.id) {
        Bus.$emit('openModal', {status: 'error', message: 'Unable to identify schedule'})
        return
      }

      if (!confirm('Cancelling is not reversible, are you absolutely sure you would like to cancel this installment schedule?')) {
        return
      }

      try {
        Bus.$emit('openWaitModal')
        let result = await this.api({
          method: 'delete',
          url: `/schedules/${this.schedule.id}`
        })

        if (result.data && !result.data.error) {
          Bus.$emit('openModal', {status: 'success', message: 'Installment schedule has been cancelled'})
          this.load()
        } else {
          throw new Error('Error while cancelling schedule')
        }
      } catch (err) {
        Bus.$emit('openModal', {status: 'error', message: err})
      } finally {
        Bus.$emit('closeWaitModal')
      }
    },

    takePayment () {
      this.payModal = true
    },

    payTypeChanged (value, id) {
      if (id === 0) {
        this.payType = [true, false]
      } else if (id === 1) {
        this.payType = [false, true]
      }
    },

    setPayStep () {
      if (this.payType[0]) {
        this.payStep = 2
      } else if (this.payType[1]) {
        this.payStep = 3
      }
    },

    closePayModal () {
      this.payStep = 1
      this.payType = [true, false]
      this.payModal = false
    },

    modifySchedule () {
      this.modifyModal = true
    },

    modifyTypeChanged (value, id) {
      if (value && id === 0) {
        this.modifyType = [true, false, false]
      } else if (value && id === 1) {
        this.modifyType = [false, true, false]
      } else if (value && id === 2) {
        this.modifyType = [false, false, true]
      }
    },

    setModifyStep () {
      if (this.modifyType[0]) {
        this.modifyStep = 2
      } else if (this.modifyType[1]) {
        this.modifyStep = 3
      } else if (this.modifyType[2]) {
        this.modifyStep = 4
      }
    },

    closeModifyModal () {
      this.modifyStep = 1
      this.modifyModal = false
    }
  }
}
</script>

<style lang="less" scoped>
  .v-expansion-panel__container {
    margin: 5px;
  }
  .v-expansion-panel__header {
    background: var(--v-secondary-base) !important;
    padding: 5px;
  }

  .overflow-button {
    padding-bottom: 20px;
    padding-left: 5px;
    padding-right: 5px;
    padding-top: 20px;
  }

  .overflow-button .v-btn__content span {
    overflow: hidden;
    text-overflow: ellipsis !important;
    white-space: nowrap;
  }
</style>



// WEBPACK FOOTER //
// src/components/pnxInstallments/Manage.vue